import { useEffect, useState, useCallback } from "react";
import api, { API, apiError, pollJob } from "@/lib/api";
import { toast } from "sonner";
import { copyText } from "@/lib/clipboard";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Landmark, Sparkles, Loader2, Eye, Download, FileText, Copy, Trash2, Send } from "lucide-react";

export default function GovernmentLetters({ clientId }) {
  const [agencies, setAgencies] = useState([]);
  const [letters, setLetters] = useState([]);
  const [form, setForm] = useState({ agency_key: "cfpb", subject: "", details: "", custom_recipient_name: "", custom_recipient_address: "" });
  const [generating, setGenerating] = useState(false);
  const [view, setView] = useState(null);

  const load = useCallback(() => {
    api.get(`/clients/${clientId}/gov-letters`).then((r) => setLetters(r.data)).catch(() => {});
  }, [clientId]);

  useEffect(() => {
    api.get("/government-agencies").then((r) => setAgencies(r.data)).catch(() => {});
    load();
  }, [clientId, load]);

  const selected = agencies.find((a) => a.key === form.agency_key);
  const isCustom = form.agency_key === "custom";
  const needsAddress = !!selected?.needs_address;

  const generate = async () => {
    if (isCustom && !form.custom_recipient_name.trim()) return toast.error("Enter the recipient agency name");
    setGenerating(true);
    try {
      const { data } = await api.post(`/clients/${clientId}/gov-letters`, form);
      await pollJob(data.job_id);
      toast.success("Government letter drafted");
      setForm((f) => ({ ...f, subject: "", details: "" }));
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    } finally {
      setGenerating(false);
    }
  };

  const remove = async (id) => { await api.delete(`/gov-letters/${id}`); load(); };

  const url = (id, fmt) => `${API}/gov-letters/${id}/render?fmt=${fmt}&auth=${encodeURIComponent(localStorage.getItem("cp_token") || "")}`;
  const preview = (id) => window.open(url(id, "html"), "_blank");
  const grab = (id, fmt) => {
    const a = document.createElement("a");
    a.href = url(id, fmt);
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <div className="space-y-6" data-testid="gov-letters-tab">
      <Card className="p-6 bg-gradient-to-br from-primary/15 via-card/60 to-transparent border-primary/30" data-testid="gov-letter-create-card">
        <div className="flex items-start gap-3 mb-5">
          <div className="h-11 w-11 rounded-lg bg-primary/20 flex items-center justify-center shrink-0"><Landmark className="h-6 w-6 text-primary" /></div>
          <div>
            <h3 className="font-display text-xl font-semibold">Letters to Government Agencies</h3>
            <p className="text-sm text-muted-foreground mt-1 max-w-xl">Draft a formal complaint to a government agency (CFPB, FTC, OCC, your State Attorney General, or a custom recipient). AI writes it, you preview and download as PDF or Word. <span className="text-primary">Print and mail it yourself.</span></p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Agency</Label>
            <select
              value={form.agency_key}
              onChange={(e) => setForm({ ...form, agency_key: e.target.value })}
              className="w-full bg-secondary border border-border rounded-md px-3 py-2 text-sm"
              data-testid="gov-agency-select"
            >
              {agencies.map((a) => <option key={a.key} value={a.key}>{a.name}</option>)}
            </select>
            {selected && <p className="text-xs text-muted-foreground">{selected.purpose}</p>}
          </div>
          <div className="space-y-2">
            <Label>Subject</Label>
            <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="e.g. Bureau failed to correct disputed account" data-testid="gov-subject" />
          </div>

          {(isCustom || needsAddress) && (
            <>
              {isCustom && (
                <div className="space-y-2">
                  <Label>Recipient Agency Name</Label>
                  <Input value={form.custom_recipient_name} onChange={(e) => setForm({ ...form, custom_recipient_name: e.target.value })} placeholder="e.g. Department of Consumer Affairs" data-testid="gov-custom-name" />
                </div>
              )}
              <div className="space-y-2">
                <Label>Recipient Mailing Address</Label>
                <Textarea value={form.custom_recipient_address} onChange={(e) => setForm({ ...form, custom_recipient_address: e.target.value })} placeholder="Street, City, State ZIP" rows={2} data-testid="gov-custom-address" />
              </div>
            </>
          )}

          <div className="space-y-2 sm:col-span-2">
            <Label>What happened / details</Label>
            <Textarea value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })} placeholder="Describe the issue: which bureau/furnisher, what was disputed, dates, and what went wrong. Leave blank for a general FCRA complaint." rows={4} data-testid="gov-details" />
          </div>
        </div>

        <div className="mt-5">
          <Button onClick={generate} disabled={generating} className="rounded-lg" data-testid="gov-generate-btn">
            {generating ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Drafting…</> : <><Sparkles className="h-4 w-4 mr-2" /> Generate Letter</>}
          </Button>
        </div>
      </Card>

      <div className="flex items-center justify-between">
        <h3 className="font-display text-xl font-semibold">Drafted Letters</h3>
        <Badge variant="secondary" className="text-muted-foreground"><Send className="h-3 w-3 mr-1" /> Print &amp; mail manually</Badge>
      </div>

      <div className="grid gap-3">
        {letters.map((l) => (
          <Card key={l.id} className="p-4 bg-card/60 border-border" data-testid={`gov-letter-${l.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium">{l.agency_name}</span>
                  <Badge className="bg-amber-500/20 text-amber-300 text-xs">Draft</Badge>
                </div>
                <div className="text-sm text-muted-foreground mt-1">RE: {l.subject}</div>
                <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{l.content?.slice(0, 160)}…</div>
              </div>
              <Button size="icon" variant="ghost" className="text-destructive shrink-0" onClick={() => remove(l.id)} data-testid={`gov-delete-${l.id}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
            <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-border">
              <Button size="sm" className="rounded-lg" onClick={() => setView(l)} data-testid={`gov-view-${l.id}`}><Eye className="h-3.5 w-3.5 mr-1" /> View</Button>
              <Button size="sm" variant="secondary" className="rounded-lg" onClick={() => preview(l.id)} data-testid={`gov-preview-${l.id}`}><FileText className="h-3.5 w-3.5 mr-1" /> Preview</Button>
              <Button size="sm" variant="secondary" className="rounded-lg" onClick={() => grab(l.id, "pdf")} data-testid={`gov-pdf-${l.id}`}><Download className="h-3.5 w-3.5 mr-1" /> PDF</Button>
              <Button size="sm" variant="secondary" className="rounded-lg" onClick={() => grab(l.id, "doc")} data-testid={`gov-word-${l.id}`}><FileText className="h-3.5 w-3.5 mr-1" /> Word</Button>
              <Button size="sm" variant="secondary" className="rounded-lg" onClick={() => copyText(l.content, "Letter copied")} data-testid={`gov-copy-${l.id}`}><Copy className="h-3.5 w-3.5" /></Button>
            </div>
          </Card>
        ))}
        {!letters.length && <p className="text-sm text-muted-foreground py-8 text-center">No government letters yet. Draft one above.</p>}
      </div>

      <Dialog open={!!view} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent className="bg-card/95 backdrop-blur-xl border-border max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display flex items-center justify-between pr-6">
            <span className="truncate">{view?.agency_name}</span>
            <Button size="sm" variant="secondary" onClick={() => copyText(view?.content, "Letter copied")} data-testid="gov-copy-dialog"><Copy className="h-4 w-4 mr-1" /> Copy</Button>
          </DialogTitle></DialogHeader>
          <div className="text-xs text-muted-foreground whitespace-pre-line mb-2">{view?.agency_address}</div>
          <div className="text-sm font-semibold mb-2">RE: {view?.subject}</div>
          <pre className="whitespace-pre-wrap font-mono text-xs leading-relaxed bg-background/50 p-5 rounded-lg border border-border" data-testid="gov-letter-content">{view?.content}</pre>
        </DialogContent>
      </Dialog>
    </div>
  );
}
